// arise-frontend/src/context/AuthContext.jsx
// Single source of truth for the signed-in user. Token storage itself lives in
// services/api.js; this provider only mirrors it into React state.
//
// status: 'loading'     - a stored token is being validated via GET /auth/me
//         'authenticated'
//         'anonymous'   - no (valid) session
//         'unavailable' - token exists but could not be verified (network/5xx/429);
//                         the token is kept, nothing is cleared
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  TOKEN_STORAGE_KEY,
  getAccessToken,
  login as apiLogin,
  fetchCurrentUser,
  remoteLogout,
  discardSession,
  abortPrivateRequests,
  onSessionExpired,
} from '../services/api';

const AuthContext = createContext(null);

const EXPIRED_NOTICE  = 'Your session has expired. Please log in again.';
const INACTIVE_NOTICE = 'This account is inactive.';

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [status, setStatus] = useState(() => (getAccessToken() ? 'loading' : 'anonymous'));
  const [notice, setNotice] = useState(null);
  const alive = useRef(false);   // false after unmount (StrictMode re-sets it on remount)
  const seq   = useRef(0);       // latest-request-wins guard for async validation

  const restore = useCallback(async () => {
    const id = ++seq.current;
    if (!getAccessToken()) {
      setUser(null);
      setStatus('anonymous');
      return;
    }
    setStatus('loading');
    try {
      const me = await fetchCurrentUser();
      if (!alive.current || id !== seq.current) return;
      setUser(me);
      setStatus('authenticated');
    } catch (e) {
      if (!alive.current || id !== seq.current) return;
      setUser(null);
      if (e && e.status === 401) {
        // api.js already cleared the token and emitted the expiry notice.
        setStatus('anonymous');
      } else if (e && e.status === 403) {
        setStatus('anonymous');
        setNotice(INACTIVE_NOTICE);
      } else {
        setStatus('unavailable');   // keep the token; allow retry
      }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    restore();

    // Protected request got 401 for the current token.
    const offExpired = onSessionExpired(() => {
      seq.current += 1;
      setUser(null);
      setStatus('anonymous');
      setNotice(EXPIRED_NOTICE);
    });

    // Cross-tab: another tab logged in/out. Drop private state immediately,
    // then re-validate whatever token is there now.
    const onStorage = (e) => {
      if (e.key !== null && e.key !== TOKEN_STORAGE_KEY) return;
      abortPrivateRequests();
      setUser(null);
      if (getAccessToken()) {
        restore();
      } else {
        seq.current += 1;
        setStatus('anonymous');
        setNotice(null);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      alive.current = false;
      offExpired();
      window.removeEventListener('storage', onStorage);
    };
  }, [restore]);

  const login = useCallback(async (email, password) => {
    const u = await apiLogin(email, password);
    seq.current += 1;
    setUser(u);
    setStatus('authenticated');
    setNotice(null);
    return u;
  }, []);

  const logout = useCallback(() => {
    const token = getAccessToken();
    discardSession();            // clear token + abort private in-flight requests
    seq.current += 1;
    setUser(null);
    setStatus('anonymous');
    setNotice(null);
    remoteLogout(token);         // best-effort, not awaited
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  const value = useMemo(
    () => ({ user, status, notice, login, logout, refresh: restore, clearNotice }),
    [user, status, notice, login, logout, restore, clearNotice]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
